import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import PartsEditorTable from "../../components/partsEditor/PartsEditorTable";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import PopOutBox from "../../components/shared/PopOutBox";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCategoriesPartial from "../../data/useCategoriesPartial";
import useWarehouseLocations from "../../data/useWarehouseLocations";
import { IPart, IPartCategory } from "../../models/Part";
import ComboBoxInput from "../../components/shared/Inputs/ComboBoxInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { ILocation, IWarehouseLocation } from "../../models/Location";

export default function PartsEditor() {
  const { setInfo } = usePageNameContext();
  const { categoryId } = useParams();
  const { categories: allCategories } = useCategoriesPartial();
  const { warehouseLocations: allLocations } = useWarehouseLocations();
  const [category, setCategory] = useState<IPartCategory | undefined>(undefined);
  const [location, setLocation] = useState<IWarehouseLocation | undefined>(undefined);
  const [parts, setParts] = useState<IPart[] | undefined>(undefined);
  const [part, setPart] = useState<IPart | undefined>(undefined);
  const [includes, setIncludes] = useState("");
  const [excludes, setExcludes] = useState("");
  const [regex, setRegex] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const [additionalBody, setAdditionalBody] = useState<Record<string, any>>({});
 
  useEffect(() => {
    setInfo({
      name: "Parts Editor",
      desc: "View and edit your parts here",
    });
  }, []);

  useEffect(() => {
    if (!categoryId) return;
    setCategory(allCategories.data?.data.find((category) =>
      category.id === categoryId
    ));
  }, [categoryId]);

  useEffect(() => {
    if (includes.length > 0 || excludes.length > 0) {
      let includesFormatted = includes.replace(/\s+/g, '').split(",").join("|");
      let excludesFormatted = excludes.replace(/\s+/g, '').split(",").join("|");

      let newRegex = "";

      if (excludes.length > 0 && includes.length > 0) {
        newRegex = `(?<!(${excludesFormatted}).*)(?:${includesFormatted})(?!.*(${excludesFormatted}))`;
      } else if (excludes.length > 0 && includes.length === 0) {
        newRegex = `^(?!.*(${excludesFormatted})).*$`
      } else {
        newRegex = `(?:${includesFormatted})`;
      }

      setRegex(newRegex);

      // let regexExp = new RegExp(newRegex, "i");

      setAdditionalBody({
        ...additionalBody,
        regex: newRegex,
        include: includes,
        exclude: excludes,
      })      
    } else {
      // setRegex("");
      // setParts(categoryParts.data?.data);
    }
  }, [includes, excludes]);

  // useEffect(() => {
  //   if(category == undefined) return;
  //   categoryParts.refetch();
  // }, [category])

  const handleSetPart = (part: IPart) => {
    setPart(part);
    setCreateOpen(true);
  };

  if (allCategories.isLoading || !allCategories.data || allLocations.isLoading || !allLocations.data) return (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  )

  return (
    <>
      <div className="mt-4">
        <h1 className="text-lg">Category: {category?.name}</h1>
      </div>
      <div className="mt-4 flex justify-between">
        <div className="flex gap-x-2">
          <ComboBoxInput
            options={[
              { id: "-2", name: "Uncategorised"  },
              { id: "-1", name: "All Parts" },
              ...(allCategories.data?.data || []),
            ]}
            setValue={(registerName: string, value: IPartCategory) => {
              // if (!value) return;
              let catId: string | null = value.id;
              if (catId === "-1") catId = null; // Use a placeholder for "All Parts"
              if (catId === "-2") catId = "00000000-0000-0000-0000-000000000000";
              setCategory(value);
              setAdditionalBody({
                ...additionalBody,
                categoryId: catId
              })
            }}
            registerName={"category"}
            register={undefined}
            searchField={"name"}
            initialValue={categoryId ?? "-1"} // Default to "All Parts" if no categoryId
          />
          <ComboBoxInput
            options={[
              { id: "-2", name: "No Location"  },
              { id: "-1", name: "All Locations" },
              ...(allLocations.data?.data.map(x => { return {
                id: x.id,
                name: x.displayName
            }}) || []),
            ]}
            setValue={(registerName: string, value: IWarehouseLocation) => {
              // if (!value) return;
              let locationId: string | null = value?.id;
              if (locationId === "-1") locationId = null; // Use a placeholder for "All Parts"
              if (locationId === "-2") locationId = "00000000-0000-0000-0000-000000000000";
              setLocation(value);
              setAdditionalBody({
                ...additionalBody,
                locationId: locationId
              })
            }}
            registerName={"location"}
            register={undefined}
            searchField={"name"}
            initialValue={categoryId ?? "-1"} // Default to "All Parts" if no categoryId
          />
          <input
            type="text"
            onChange={(e) => setIncludes(e.target.value)}
            className="sm:text-sm"
            placeholder="Include"
          />
          <input
            type="text"
            onChange={(e) => setExcludes(e.target.value)}
            className="sm:text-sm"
            placeholder="Exclude"
          />
{/* 
          <input
            type="text"
            value={regex}
            className="sm:text-sm text-gray-500"
            placeholder="Regex"
            disabled
          /> */}

          {/* Add checkbox or switch for accepted */}
          {/* <ButtonInput label={"Copy"} isSubmit={false} classes="" onClick={() => navigator.clipboard.writeText(regex)} />
          <PopOutBox classes="w-96">
            <p>Comma separate your include and exclude words to generate regex and see the results in real time.</p>
            <p>E.g: Includes - petrol, pump, compressor</p>
            <p>Excludes - diesel, door, mirror</p>
          </PopOutBox> */}
          
          <div className="flex items-center ml-6">
            <input onChange={(e) => 
              setAdditionalBody({
                ...additionalBody,
                accepted: e.target.checked
              })} 
              checked={additionalBody.accepted || false} 
              type="checkbox" 
              className="mr-2 h-5 w-5" 
            />
            <label>Accepted</label>
          </div>

          <div className="flex items-center ml-6">
            <input onChange={(e) => 
              setAdditionalBody({
                ...additionalBody,
                noImages: e.target.checked
              })} 
              checked={additionalBody.noImages || false} 
              type="checkbox" 
              className="mr-2 h-5 w-5" 
            />
            <label>No Images</label>
          </div>
        </div>
      </div>
      <PartsEditorTable parts={parts!} setPart={handleSetPart} additionalBody={additionalBody} />  
    </>
  );
}
